import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Scale, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPenaltySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Promil bantları için mesajlar
  const [safeBand, setSafeBand] = useState("");
  const [borderBand, setBorderBand] = useState("");
  const [illegalBand, setIllegalBand] = useState("");
  const [criticalBand, setCriticalBand] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/penalty-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setSafeBand(data.safe_message || "");
      setBorderBand(data.border_penalty || "");
      setIllegalBand(data.illegal_penalty || "");
      setCriticalBand(data.critical_penalty || "");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");
    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/penalty-settings`,
        {
          safe_message: safeBand,
          border_penalty: borderBand,
          illegal_penalty: illegalBand,
          critical_penalty: criticalBand,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Ceza bilgileri güncellendi!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-teal-400">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-penalty-settings">
      <h2 className="text-3xl font-black text-gradient">Ceza ve Uyarı Metinleri</h2>

      <div className="glass-card-premium rounded-2xl p-8 neon-border space-y-6">
        {/* Info */}
        <div className="bg-blue-500/10 border-2 border-blue-500/30 rounded-2xl p-5">
          <p className="text-blue-400 text-sm font-semibold mb-2">
            <Scale className="w-4 h-4 inline mr-2" />
            Promil Bantlarına Göre Uyarı Metinleri
          </p>
          <p className="text-gray-300 text-xs leading-relaxed">
            Her promil bandı için gösterilecek uyarı ve ceza bilgilerini düzenleyin.<br />
            Satır başları otomatik korunur (\\n ile).
          </p>
        </div>

        {/* Safe Band (< 0.40) */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-green-400 uppercase tracking-wider flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            Güvenli Bant ({"< 0.40‰"})
          </Label>
          <Textarea
            value={safeBand}
            onChange={(e) => setSafeBand(e.target.value)}
            placeholder="Yasal sınırın altındasınız..."
            rows={3}
            className="rounded-2xl bg-gray-900/50 border-2 border-green-500/30 text-white text-sm"
            data-testid="safe-band-input"
          />
        </div>

        {/* Border Band (0.40-0.49) */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-yellow-400 uppercase tracking-wider flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            Sınır Bant (0.40 - 0.49‰)
          </Label>
          <Textarea
            value={borderBand}
            onChange={(e) => setBorderBand(e.target.value)}
            placeholder="Uyarılar ve ceza bilgisi..."
            rows={4}
            className="rounded-2xl bg-gray-900/50 border-2 border-yellow-500/30 text-white text-sm"
            data-testid="border-band-input"
          />
        </div>

        {/* Illegal Band (0.50-0.99) */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-red-400 uppercase tracking-wider flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            Yasal Dışı Bant (0.50 - 0.99‰)
          </Label>
          <Textarea
            value={illegalBand}
            onChange={(e) => setIllegalBand(e.target.value)}
            placeholder="• İlk yakalanmada tahmini ceza: 9.268 TL\\n• Ehliyetinize 6 ay süreyle el konulur..."
            rows={5}
            className="rounded-2xl bg-gray-900/50 border-2 border-red-500/30 text-white text-sm"
            data-testid="illegal-band-input"
          />
        </div>

        {/* Critical Band (≥ 1.00) */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-rose-400 uppercase tracking-wider flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-600"></div>
            Kritik Bant (≥ 1.00‰) - ADLİ SÜREÇ
          </Label>
          <Textarea
            value={criticalBand}
            onChange={(e) => setCriticalBand(e.target.value)}
            placeholder="TCK 179 kapsamında adli süreç riski..."
            rows={8}
            className="rounded-2xl bg-gray-900/50 border-2 border-rose-500/30 text-white text-sm"
            data-testid="critical-band-input"
          />
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black text-lg shadow-lg neon-glow"
          data-testid="save-penalty-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? "Kaydediliyor..." : "Ceza Bilgilerini Kaydet"}
        </Button>
      </div>
    </div>
  );
};

export default AdminPenaltySettings;
